import { useState, useEffect } from 'react';
import { FaQuestionCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import {IoIosAddCircleOutline, IoMdCreate, IoMdTrash} from 'react-icons/io'; 
import AñadirPregunta from './AnadirPregunta';
import EditarPregunta from './EditarPregunta';
import Swal from 'sweetalert2';
import styles from './Preguntas.module.css'
import {useAuth} from '../../../Auth/AuthContext'; 

const Preguntas = ({ testId, currentPage, setCurrentPage, preguntasPorPagina }) => {
    const [preguntas, setPreguntas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [showModalAgregar, setShowModalAgregar] = useState(false);
    const [showModalEditar, setShowModalEditar] = useState(false);
    const [preguntaAEditar, setPreguntaAEditar] = useState(null);
    const [areasDisponibles, setAreasDisponibles] = useState([]);

    const paginaActual = currentPage || 1;
    const setPaginaActual = setCurrentPage || (() => {});
    const preguntasPorPag = preguntasPorPagina || 10;

    useEffect(() => {
        if (testId) {
            fetchPreguntas();
            fetchAreas();
        }
    }, [testId]);

    const fetchPreguntas = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${testId}/preguntas`, { 
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMessage = 'Error desconocido al obtener preguntas';
                let errorDetails = '';

                try {
                    const errorData = await response.json();
                    if (response.status === 400) {
                        errorMessage = 'ID de test inválido';
                        errorDetails = 'El ID del test debe ser un número válido';
                    } else if (response.status === 500) {
                        errorMessage = 'Error interno del servidor';
                        errorDetails = 'No se pudieron obtener las preguntas. Inténtalo más tarde.';
                    } else {
                        errorMessage = errorData.error || `Error ${response.status}`;
                        errorDetails = errorData.message || response.statusText;
                    }
                } catch (parseError) {
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                    errorDetails = 'No se pudo procesar la respuesta del servidor';
                }

                await Swal.fire({
                    icon: 'error',
                    title: errorMessage,
                    text: errorDetails,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });

                throw new Error(`${errorMessage}: ${errorDetails}`);
            }

            const data = await response.json();
            setPreguntas(data);
        } catch (err) {
            setError(err.message);
            console.error('Error al obtener preguntas:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAreas = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${testId}/areas`, { 
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMessage = 'Error al obtener áreas';
                let errorDetails = '';

                try {
                    const errorData = await response.json();
                    if (response.status === 400) {
                        errorMessage = errorData.error || 'ID de test inválido';
                        errorDetails = errorData.message || 'El ID del test debe ser un número válido';
                    } else if (response.status === 500) {
                        errorMessage = errorData.error || 'Error interno del servidor';
                        errorDetails = errorData.message || 'No se pudieron obtener las áreas';
                    } else {
                        errorMessage = errorData.error || `Error ${response.status}`;
                        errorDetails = errorData.message || response.statusText;
                    }
                } catch (parseError) {
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                    errorDetails = 'No se pudo procesar la respuesta del servidor';
                }

                console.error('Error al obtener áreas:', errorMessage, errorDetails);

                await Swal.fire({
                    icon: 'warning',
                    title: 'Problema al cargar áreas',
                    text: `${errorMessage}. ${errorDetails}`,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });

                return;
            }

            const areas = await response.json();
            setAreasDisponibles(areas);
        } catch (error) {
            console.error('Error al obtener áreas:', error);

            await Swal.fire({
                icon: 'warning',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor para obtener las áreas disponibles.',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito', 
                }
            });
        }
    };

    const totalPreguntas = preguntas.length;
    const totalPaginas = Math.ceil(totalPreguntas / preguntasPorPag);
    const indiceInicio = (paginaActual - 1) * preguntasPorPag;
    const indiceFin = indiceInicio + preguntasPorPag;
    const preguntasActuales = preguntas.slice(indiceInicio, indiceFin);

    const irAPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
        const preguntasContainer = document.querySelector('.preguntas-container');
        if (preguntasContainer) {
            preguntasContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const paginaAnterior = () => {
        if (paginaActual > 1) {
            irAPagina(paginaActual - 1);
        }
    };

    const paginaSiguiente = () => {
        if (paginaActual < totalPaginas) {
            irAPagina(paginaActual + 1);
        }
    };

    const handleAddPregunta = () => {
        setShowModalAgregar(true);
    };

    const handleCloseModal = () => {
        setShowModalAgregar(false);
    };

    const handlePreguntaAdded = () => {
        fetchPreguntas();
        setShowModalAgregar(false);

        setTimeout(() => {
            const addButton = document.querySelector('.btn-add-pregunta');
            if (addButton) {
                addButton.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                addButton.style.transform = 'scale(1.05)';
                addButton.style.transition = 'transform 0.3s ease';
                setTimeout(() => {
                    addButton.style.transform = 'scale(1)';
                }, 300);
            }
        }, 100);
    };

    const handleEditPregunta = (pregunta) => {
        setPreguntaAEditar(pregunta);
        setShowModalEditar(true);
    };

    const handleCloseModalEditar = () => {
        setShowModalEditar(false);
        setPreguntaAEditar(null);
    };

    const handlePreguntaUpdated = () => {
        fetchPreguntas();
    };

    const handleDeletePregunta = async (preguntaId) => {
        const result = await Swal.fire({
            title: '¿Eliminar pregunta?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#242b57',
            customClass: {
                popup: 'popupbonito'
            },
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/test/preguntas/eliminar-pregunta/${preguntaId}`, {
                    method: 'DELETE',
                    headers: {
                    'Authorization': `Bearer ${token}`
                },
                });

                if (!response.ok) {
                    let errorMessage = 'Error al eliminar pregunta';
                    let errorDetails = '';

                    try {
                        const errorData = await response.json();
                        if (response.status === 400) {
                            errorMessage = errorData.error || 'ID de pregunta inválido';
                            errorDetails = 'El ID de la pregunta debe ser un número válido';
                        } else if (response.status === 404) {
                            errorMessage = errorData.error || 'Pregunta no encontrada';
                            errorDetails = 'La pregunta que intentas eliminar no existe o ya fue eliminada';
                        } else if (response.status === 500) {
                            if (errorData.error && errorData.error.includes('eliminar pregunta')) {
                                errorMessage = 'Error interno del servidor al eliminar pregunta';
                                errorDetails = 'Ocurrió un problema en la base de datos. Inténtalo más tarde.';
                            } else {
                                errorMessage = errorData.error || 'Error interno del servidor';
                                errorDetails = 'Ocurrió un problema inesperado. Inténtalo más tarde.';
                            }
                        } else {
                            errorMessage = errorData.error || `Error ${response.status}`;
                            errorDetails = errorData.message || response.statusText;
                        }
                    } catch (parseError) {
                        errorMessage = `Error ${response.status}: ${response.statusText}`;
                        errorDetails = 'No se pudo procesar la respuesta del servidor';
                    }

                    await Swal.fire({
                        icon: 'error',
                        title: errorMessage,
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#242b57', 
                        customClass: { 
                            popup: 'popupbonito', 
                        },
                    });

                    throw new Error(`${errorMessage}: ${errorDetails}`);
                }

                setPreguntas(preguntas.filter(p => p.pr_clave !== preguntaId));

                await Swal.fire({
                    title: 'Pregunta Eliminada',
                    text: 'La pregunta ha sido eliminada',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false, 
                    customClass: {
                        popup: 'popupbonito', 
                    },
                });

                const nuevaTotalPreguntas = totalPreguntas - 1;
                const nuevaTotalPaginas = Math.ceil(nuevaTotalPreguntas / preguntasPorPag);
                if (paginaActual > nuevaTotalPaginas && nuevaTotalPaginas > 0) {
                    setPaginaActual(nuevaTotalPaginas);
                }

            } catch (error) {
                console.error('Error al eliminar pregunta:', error);
            }
        }
    };

    const getMaxOrden = () => {
        return preguntas.length > 0 ? Math.max(...preguntas.map(p => p.pr_orden || 0)) : 0;
    };

    const renderPaginacion = () => {
        if (totalPaginas <= 1) return null;

        const getPaginasVisibles = () => {
            const delta = 2;
            const range = [];

            if (totalPaginas <= 7) {
                for (let i = 1; i <= totalPaginas; i++) {
                    range.push(i);
                }
                return range;
            }

            const start = Math.max(1, paginaActual - delta);
            const end = Math.min(totalPaginas, paginaActual + delta);

            if (start > 1) {
                range.push(1);
                if (start > 2) {
                    range.push('...');
                }
            }

            for (let i = start; i <= end; i++) {
                range.push(i);
            }

            if (end < totalPaginas) {
                if (end < totalPaginas - 1) {
                    range.push('...');
                }
                range.push(totalPaginas);
            }

            return range;
        };

        const paginasVisibles = getPaginasVisibles();

        return (
            <div className={styles.paginacion}>
                <button
                    className={styles.btnPaginacion}
                    onClick={paginaAnterior}
                    disabled={paginaActual === 1}
                >
                    <FaChevronLeft />
                    <span>Anterior</span>
                </button>

                <div className={styles.numerosPagina}>
                    {paginasVisibles.map((numeroPagina, index) => {
                        if (numeroPagina === '...') {
                            return (
                                <span key={`ellipsis-${index}`} className="ellipsis">
                                    ...
                                </span>
                            );
                        }

                        return (
                            <button
                                key={numeroPagina}
                                className={`${styles.btnNumero} ${paginaActual === numeroPagina ? styles.active : ''}`}
                                onClick={() => irAPagina(numeroPagina)}
                            >
                                {numeroPagina}
                            </button>
                        );
                    })}
                </div>

                <button
                    className={styles.btnPaginacion}
                    onClick={paginaSiguiente}
                    disabled={paginaActual === totalPaginas}
                >
                    <span>Siguiente</span>
                    <FaChevronRight />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando preguntas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContainer}> 
            <p className={styles.loadingText}> Error al cargar datos :( </p>
            </div>
        </div>
        );
    }

    return (
        <div className={styles.preguntasContainer}>

        {/* Header de preguntas */}
        <div className={styles.preguntasHeader}>
            <div className={styles.headerInfo}>
            <h3>Configuración de Preguntas</h3>
            <p>Administra tus preguntas fácilmente: agrégalas, edítalas o elimínalas desde un solo lugar.</p>
            <p>Recuerda que debes configurar los posibles Resultados para poder asignar un Área a cada pregunta. Sin un área seleccionada, no será posible calcular los resultados del test.</p>
            </div>
        </div>

        <div className={styles.addPregunta}>
            <button className={styles.btnAddPregunta} onClick={handleAddPregunta}>
            <IoIosAddCircleOutline/>
            Añadir Pregunta
            </button>
        </div>

        {/* Información de preguntas */}
        <div className={styles.opcionesDisponibles}>
            <h4>Preguntas disponibles:</h4>
            <div className={styles.preguntasInfo}>
            <p>{totalPreguntas} pregunta{totalPreguntas !== 1 ? 's' : ''} disponible{totalPreguntas !== 1 ? 's' : ''}</p>
            {totalPaginas > 1 && (
                <p className={styles.paginationInfo}>
                Mostrando {indiceInicio + 1} - {Math.min(indiceFin, totalPreguntas)} de {totalPreguntas}
                </p>
            )}
            </div>
        </div>

        {/* Lista de preguntas o estado vacío */}
        {totalPreguntas === 0 ? (
            <div className={styles.emptyState}>
            <FaQuestionCircle className={styles.emptyIcon} />
            <h3>No hay preguntas disponibles</h3>
            <p>¡Añade tu primera pregunta!</p>
            </div>
        ) : (
            <>
            <div className={styles.preguntasList}>
                {preguntasActuales.map((pregunta, index) => (
                <div key={pregunta.pr_clave} className={styles.preguntaCard}>
                    {/* Botones de acción */}
                    <div className={styles.cardActions}>
                    <button
                        className={styles.btnEdit}
                        onClick={() => handleEditPregunta(pregunta)}
                        title="Editar pregunta"
                    >
                        <IoMdCreate/>
                    </button>
                    <button
                        className={styles.btnDelete}
                        onClick={() => handleDeletePregunta(pregunta.pr_clave)}
                        title="Eliminar pregunta"
                    >
                        <IoMdTrash/>
                    </button>
                    </div>

                    <div className={styles.preguntaHeader}>
                    <div className={styles.preguntaHeaderContent}> 
                        <div className={styles.preguntaNumber}>
                            <span>#{pregunta.pr_orden || (indiceInicio + index + 1)}</span>
                        </div>
                    </div>
                    <div>
                        {pregunta.pr_requerida ? (
                        <span className={styles.requiredBadge}>Requerida</span>
                        ) : (
                        <span className={styles.optionalBadge}>Opcional</span>
                        )}
                    </div>
                    <div>
                        <span className={styles.areaBadge}>
                        Área: {pregunta.ar_codigo || 'Sin asignar'}
                        </span>
                    </div>
                    </div>

                    <div className={styles.preguntaContent}>
                    <p>{pregunta.pr_descripcion}</p>
                    </div>
                </div>
                ))}
            </div>

            <div className={styles.preguntasSummary}>
                <div className={styles.summaryStats}>
                <div className={styles.statItem}>
                    <span className={styles.statLabel}>Total:</span>
                    <span className={styles.statValue}>{totalPreguntas}</span>
                </div>
                <div className={styles.statItem}>
                    <span className={styles.statLabel}>Requeridas:</span>
                    <span className={styles.statValue}>
                    {preguntas.filter(p => p.pr_requerida).length}
                    </span>
                </div>
                <div className={styles.statItem}>
                    <span className={styles.statLabel}>Opcionales:</span>
                    <span className={styles.statValue}>
                    {preguntas.filter(p => !p.pr_requerida).length}
                    </span>
                </div>
                </div>
            </div>
            </>
        )}

         {/* Paginación */}
            {renderPaginacion()}

        {/* Modal para Añadir Pregunta */}
        <AñadirPregunta
            testId={testId}
            onBack={handleCloseModal}
            onPreguntaAdded={handlePreguntaAdded}
            maxOrden={getMaxOrden()}
            isOpen={showModalAgregar}
        />

        {/* Modal para Editar Pregunta */}
        <EditarPregunta
            pregunta={preguntaAEditar}
            areasDisponibles={areasDisponibles}
            isOpen={showModalEditar}
            onClose={handleCloseModalEditar}
            onPreguntaUpdated={handlePreguntaUpdated}
            testId={testId}
        />
        </div>
    );
};

export default Preguntas;